"use client";

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type KeyboardEvent,
} from "react";
import type { Door } from "../_content/card";
import { Icon } from "../_family/Icon";
import { DEFAULT_LANG, LANGS, LANG_KEY, isLang } from "./lang";

/** Said on the window when the reader picks a language, so whatever follows it can. */
export const LANG_CHANGED = "vestra:lang";

function current(): Door {
  const l = document.documentElement.getAttribute("data-lang");
  return isLang(l) ? l : DEFAULT_LANG;
}

/** What the reader chose in the top bar this visit, for a browser that keeps nothing. */
let chosen: Door | null = null;

/**
 * The top bar's choice, and the only one that is kept: shows the door everywhere on the page, and keeps it
 * for the next page, when the browser lets it.
 */
export function chooseLang(door: Door): void {
  chosen = door;
  document.documentElement.setAttribute("data-lang", door);
  try {
    window.localStorage.setItem(LANG_KEY, door);
  } catch {
    // A browser that keeps nothing (a private window): the choice lasts while the page is open.
  }
  window.dispatchEvent(new Event(LANG_CHANGED));
}

/**
 * Shows a door for this page view only, keeping nothing: a link to another language's entry (a Python method,
 * a CLI command, from the search) shows that language until the reader leaves the page.
 */
export function peekLang(door: Door): void {
  document.documentElement.setAttribute("data-lang", door);
  window.dispatchEvent(new Event(LANG_CHANGED));
}

/** Puts back the reader's own choice, as the top bar keeps it, or the default when there is none. */
export function restoreLang(): void {
  let kept: string | null = null;
  try {
    kept = window.localStorage.getItem(LANG_KEY);
  } catch {
    kept = chosen;
  }
  const d = document.documentElement;
  if (isLang(kept)) d.setAttribute("data-lang", kept);
  else d.removeAttribute("data-lang");
  window.dispatchEvent(new Event(LANG_CHANGED));
}

function subscribe(onChange: () => void): () => void {
  // Another tab's choice reaches this one too.
  const fromAnotherTab = (e: StorageEvent) => {
    if (e.key !== LANG_KEY || !isLang(e.newValue)) return;
    document.documentElement.setAttribute("data-lang", e.newValue);
    onChange();
  };
  window.addEventListener(LANG_CHANGED, onChange);
  window.addEventListener("storage", fromAnotherTab);
  return () => {
    window.removeEventListener(LANG_CHANGED, onChange);
    window.removeEventListener("storage", fromAnotherTab);
  };
}

/** The door every code block shows, as the page knows it (the default until it hydrates). */
export function useLang(): Door {
  return useSyncExternalStore(subscribe, current, () => DEFAULT_LANG);
}

/** The one language switch, in the top bar: every sample on every page follows it. */
export function LangSwitch() {
  const lang = useLang();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const items = useRef<Array<HTMLButtonElement | null>>([]);
  const at = LANGS.find((l) => l.door === lang) ?? LANGS[0]!;

  // Arriving from another part of the site, the inline script has not run: apply the kept choice before the
  // first paint.
  useLayoutEffect(() => {
    const d = document.documentElement;
    d.setAttribute("data-docs", "js");
    if (d.hasAttribute("data-lang")) return;
    try {
      const kept = window.localStorage.getItem(LANG_KEY);
      if (isLang(kept)) {
        d.setAttribute("data-lang", kept);
        window.dispatchEvent(new Event(LANG_CHANGED));
      }
    } catch {
      // Nothing kept: the default stays.
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    items.current[LANGS.findIndex((l) => l.door === lang)]?.focus();
    const away = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("click", away);
    return () => document.removeEventListener("click", away);
  }, [open, lang]);

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = items.current.findIndex((b) => b === document.activeElement);
    const to = (n: number) => {
      e.preventDefault();
      items.current[n]?.focus();
    };
    if (e.key === "ArrowDown") to((i + 1) % LANGS.length);
    else if (e.key === "ArrowUp") to((i + LANGS.length - 1) % LANGS.length);
    else if (e.key === "Home") to(0);
    else if (e.key === "End") to(LANGS.length - 1);
    else if (e.key === "Escape") {
      setOpen(false);
      button.current?.focus();
    } else if (e.key === "Tab") {
      // Leaving the menu by Tab closes it; focus moves on as Tab says.
      setOpen(false);
    }
  };

  return (
    <div className={open ? "lang open" : "lang"} ref={wrap}>
      <button
        ref={button}
        className="lang-btn"
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="vf-lang-menu"
        aria-label={`Code language: ${at.label}`}
        onClick={() => setOpen((o) => !o)}
      >
        {/* All five, the stylesheet showing the reader's from the first paint, as it does every code block. */}
        {LANGS.map((l) => (
          <span key={l.door} className="lc" data-l={l.door}>
            <span className="lv">
              <Icon name={l.icon} />
              <span className="long">{l.label}</span>
              <span className="short" aria-hidden="true">
                {l.short}
              </span>
            </span>
          </span>
        ))}
        <Icon name="caretDown" className="car" />
      </button>
      <div className="lang-pop">
        <div
          className="lang-menu"
          id="vf-lang-menu"
          role="menu"
          aria-label="Code language"
          onKeyDown={onKey}
        >
          {LANGS.map((l, i) => (
            <button
              key={l.door}
              ref={(el) => {
                items.current[i] = el;
              }}
              type="button"
              role="menuitemradio"
              aria-checked={l.door === lang}
              tabIndex={open ? 0 : -1}
              onClick={() => {
                chooseLang(l.door);
                setOpen(false);
                button.current?.focus();
              }}
            >
              <span className="t">
                <Icon name={l.icon} />
              </span>
              <span>
                <b>{l.label}</b>
                <small>{l.says}</small>
              </span>
              <Icon name="check" className="chk" />
            </button>
          ))}
        </div>
        <p>Every sample on every page follows this. Change it any time.</p>
      </div>
    </div>
  );
}
